import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Filter, Eye, Pencil, BookOpen, Star, ArrowUp, ArrowDown } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { PaginationBar, EmptyState, LoadingBlock } from "@/components/Pagination";
import { RowActionsMenu } from "@/components/admin/RowActionsMenu";
import { StatusFilterTabs } from "@/components/admin/StatusFilterTabs";
import { useLifecycleActions } from "@/components/admin/useLifecycleActions";
import { api, formatApiError } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 15;
const TYPES = ["self_paced", "live", "blended", "certification", "compliance"];
const LEVELS = ["beginner", "intermediate", "advanced"];
const STATUSES = ["draft", "published", "archived", "suspended"];

export default function AdminCoursesListPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [type, setType] = useState("all");
  const [level, setLevel] = useState("all");
  const [status, setStatus] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, status_filter: statusFilter };
      if (search) params.search = search;
      if (category !== "all") params.category_id = category;
      if (type !== "all") params.course_type = type;
      if (level !== "all") params.course_level = level;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/admin/courses", { params });
      setRows(data.data.courses); setTotal(data.data.total);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, search, category, type, level, status, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const lc = useLifecycleActions({
    basePath: "/admin/courses",
    labelOf: (row) => `"${row.course_title}"`,
    onDone: load,
  });

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/course-categories"); setCats(data.data.categories); }
      catch { /* silent */ }
    })();
  }, []);

  const clearFilters = () => { setSearch(""); setCategory("all"); setType("all"); setLevel("all"); setStatus("all"); setPage(1); };

  const move = async (course, direction) => {
    try {
      const { data } = await api.patch(`/admin/courses/${course.id}/move`, null, { params: { direction } });
      if (data.data?.moved === false) {
        toast.message(data.message || `Already at the ${direction === "up" ? "top" : "bottom"}`);
      } else {
        toast.success(`Moved "${course.course_title}" ${direction}`);
        load();
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="Courses" breadcrumbs={["Platform", "Courses"]}>
      <div className="space-y-4" data-testid="admin-courses-list">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <StatusFilterTabs
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              testId="courses-status-filter"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search by course title or code" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="admin-course-search" />
            </div>
            <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
              <SelectTrigger className="w-52"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.category_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {TYPES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={level} onValueChange={(v) => { setLevel(v); setPage(1); }}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Level" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All levels</SelectItem>
                {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {[search, category, type, level, status].filter((v) => v && v !== "all").length > 0 && (
              <button className="text-xs font-semibold text-slate-500 hover:text-[#101876]" onClick={clearFilters}>Clear</button>
            )}
            <div className="ml-auto">
              <button onClick={() => navigate("/app/super-admin/courses/new")} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="admin-course-add-btn">
                <Plus className="h-4 w-4" /> New course
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? <LoadingBlock label="Loading courses…" /> : rows.length === 0 ? (
            <EmptyState title="No courses match" description="Adjust filters or create your first course." icon={BookOpen} />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-[90px]">Order</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Course</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Category</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Level</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Duration</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Mapped</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((c, idx) => (
                  <TableRow key={c.id} data-testid={`admin-course-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-1" data-testid={`course-order-${c.id}`}>
                        <button type="button"
                          onClick={() => move(c, "up")}
                          disabled={page === 1 && idx === 0}
                          title="Move up"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`course-move-up-${c.id}`}>
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button"
                          onClick={() => move(c, "down")}
                          disabled={page * PAGE_SIZE >= total && idx === rows.length - 1}
                          title="Move down"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`course-move-down-${c.id}`}>
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {c.featured && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />}
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{c.course_title}</div>
                          <div className="text-xs text-slate-500 font-mono truncate">{c.course_code}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{c.category_name || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600 capitalize">{c.course_type?.replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-xs text-slate-600 capitalize">{c.course_level}</TableCell>
                    <TableCell className="text-xs text-slate-600">{c.estimated_duration_hours}h</TableCell>
                    <TableCell className="text-sm font-semibold text-[#101876]">{c.mapped_orgs_count || 0}</TableCell>
                    <TableCell><StatusBadge status={c.deleted_at ? "deleted" : c.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link to={`/app/super-admin/courses/${c.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]" data-testid={`admin-course-view-${c.id}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                        <Link to={`/app/super-admin/courses/${c.id}/edit`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]">
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <RowActionsMenu
                          testId={`admin-course-actions-${c.id}`}
                          status={c.deleted_at ? "deleted" : (c.status === "published" ? "active" : c.status)}
                          onView={() => navigate(`/app/super-admin/courses/${c.id}`)}
                          onEdit={() => navigate(`/app/super-admin/courses/${c.id}/edit`)}
                          onDeactivate={() => lc.open("deactivate", c)}
                          onReactivate={() => lc.open("reactivate", c)}
                          onDelete={() => lc.open("delete", c)}
                          onRestore={() => lc.open("restore", c)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testKey="admin-courses-pagination" />
        </div>
      </div>
      {lc.dialog}
    </DashboardLayout>
  );
}
